import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    IconButton,
    Typography,
    Grid,
    Box,
    Chip,
    Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EventIcon from '@mui/icons-material/Event';
import GavelIcon from '@mui/icons-material/Gavel';

// Hearing Status Colors Helper
const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
        case 'disposed': return 'success';
        case 'adjourned': return 'warning';
        case 'pending': return 'info';
        default: return 'default';
    }
};

const HearingDetailsDialog = ({ open, handleClose, hearingData, caseNo }) => {
    if (!hearingData) return null;

    // Dates format helper
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    return (
        <Dialog 
            open={open} 
            onClose={handleClose} 
            maxWidth="sm" 
            fullWidth
            PaperProps={{
                sx: { borderRadius: '12px', padding: 1 }
            }}
        >
            {/* Dialog Header */}
            <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                    <Typography variant="h6" component="div" sx={{ fontWeight: 'bold', color: '#2e7d32', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <GavelIcon color="success" /> Hearing Details
                    </Typography>
                    {caseNo && (
                        <Typography variant="caption" color="textSecondary">
                            Associated Case No: {caseNo}
                        </Typography>
                    )}
                </Box>
                <IconButton onClick={handleClose} aria-label="close" size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            
            <Divider />

            {/* Dialog Content */}
            <DialogContent dividers sx={{ p: 3 }}>
                <Grid container spacing={2.5}>
                    
                    {/* Status Section */}
                    <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="subtitle2" color="textSecondary">Hearing Status:</Typography>
                        <Chip 
                            label={hearingData.hearing_status || 'N/A'} 
                            color={getStatusColor(hearingData.hearing_status)} 
                            variant="contained" 
                            sx={{ fontWeight: 'bold' }}
                        />
                    </Grid>

                    {/* Schedule & Court Info */}
                    <Grid item xs={12}>
                        <Box sx={{ p: 2, bgcolor: '#f4f6f8', borderRadius: '8px', border: '1px solid #e0e0e0' }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="caption" color="textSecondary" display="block">Hearing Date</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <EventIcon fontSize="inherit" color="action" /> {formatDate(hearingData.hearing_date)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="caption" color="textSecondary" display="block">Next Hearing Date</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#d32f2f', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <EventIcon fontSize="inherit" color="error" /> {formatDate(hearingData.next_hearing_date)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="caption" color="textSecondary" display="block">Court Room</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>{hearingData.court_room || 'N/A'}</Typography>
                                </Grid>
                                {/* <Grid item xs={12} sm={6}>
                                    <Typography variant="caption" color="textSecondary" display="block">Judge Name</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>{hearingData.judge_name || 'N/A'}</Typography>
                                </Grid> */}
                            </Grid>
                        </Box>
                    </Grid>

                    {/* Case Summary */}
                    <Grid item xs={12}>
                        <Typography variant="subtitle2" sx={{ color: '#2e7d32', fontWeight: 'bold', mb: 0.5 }}>
                            Hearing Summary
                        </Typography>
                        <Box sx={{ p: 1.5, bgcolor: '#ffffff', borderRadius: '6px', border: '1px solid #eaeaea', minHeight: '60px' }}>
                            <Typography variant="body2" color={hearingData.summary ? 'textPrimary' : 'textSecondary'}>
                                {hearingData.summary || 'No summary recorded for this hearing.'}
                            </Typography>
                        </Box>
                    </Grid>

                    {/* Order Details */}
                    {/* <Grid item xs={12}>
                        <Typography variant="subtitle2" sx={{ color: '#2e7d32', fontWeight: 'bold', mb: 0.5 }}>
                            Order Details
                        </Typography>
                        <Box sx={{ p: 1.5, bgcolor: '#ffffff', borderRadius: '6px', border: '1px solid #eaeaea', minHeight: '60px' }}>
                            <Typography variant="body2" color={hearingData.order_details ? 'textPrimary' : 'textSecondary'}>
                                {hearingData.order_details || 'No specific order details updated.'}
                            </Typography>
                        </Box>
                    </Grid> */}

                    {/* Timestamps */}
                    {/* <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                        <Grid container spacing={1}>
                            <Grid item xs={6}>
                                <Typography variant="caption" color="textSecondary">Created: {formatDate(hearingData.created_at)}</Typography>
                            </Grid>
                            <Grid item xs={6} textAlign="right">
                                <Typography variant="caption" color="textSecondary">Updated: {formatDate(hearingData.updated_at)}</Typography>
                            </Grid>
                        </Grid>
                    </Grid> */}

                </Grid>
            </DialogContent>

            {/* Dialog Footer */}
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={handleClose} variant="contained" color="success" sx={{ px: 4 }}>
                    Okay
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default HearingDetailsDialog;